import React, { useEffect, useCallback, useState, useRef } from "react";
import { loader } from "helpers";

import { Link, Redirect } from "react-router-dom";
import styled from 'styled-components';
import axios from 'axios';
import moment from 'moment';
import { toast } from 'react-toastify';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronLeft, faExclamationTriangle } from '@fortawesome/free-solid-svg-icons';

const ContainerStyled = styled.div`
	
	#loader {
		width: 100px;
	}

	.page-title {
		text-align: center;
		position: relative;
		margin-bottom: 40px;

		#back-button {
			position: absolute;
			left: 0;
			top: 5px;
		}
	}

	/* Jornada de hoy: horario combinado de todas las empresas del trabajador */
	.section-card {
		background: #fff;
		border-radius: 12px;
		padding: 16px;
		margin-bottom: 15px;
		box-shadow: 0 1px 3px rgba(0, 0, 0, .1);

		.section-title {
			font-size: 12px;
			font-weight: 700;
			text-transform: uppercase;
			letter-spacing: 1px;
			color: #6c757d;
			margin-bottom: 12px;
			padding-bottom: 8px;
			border-bottom: 2px solid #eee;
		}

		.soc-block {
			background: #f7f9fc;
			border-radius: 10px;
			padding: 9px 12px;
			margin-bottom: 9px;

			.soc-h {
				font-weight: 700;
				font-size: 13px;
				margin-bottom: 6px;
				display: flex;
				align-items: center;
				gap: 7px;
			}

			.dotc {
				width: 10px;
				height: 10px;
				border-radius: 50%;
				display: inline-block;
			}

			.jornada-grid {
				display: flex;
				justify-content: space-around;
				text-align: center;

				.h { font-size: 11px; color: #6c757d; text-transform: uppercase; }
				.v { font-weight: 700; font-size: 16px; }
			}

			.off { font-size: 13px; color: #6c757d; text-align: center; }
		}
	}

	.aviso {
		background: #fff8e6;
		border: 1px solid #f6e2a4;
		color: #8a6d08;
		border-radius: 10px;
		padding: 11px 13px;
		font-size: 14px;
		margin-bottom: 16px;
	}

	/* Aviso de ubicación desactivada. Nunca impide fichar: el registro
	   simplemente se guarda sin coordenadas. */
	.aviso-geo {
		background: #fff;
		border: 1px solid #f0d7d7;
		border-left: 4px solid #dc3545;
		border-radius: 12px;
		padding: 14px 16px;
		margin-bottom: 16px;
		box-shadow: 0 1px 3px rgba(0, 0, 0, .1);
		display: flex;
		align-items: flex-start;
		gap: 12px;

		.geo-icon {
			flex: 0 0 auto;
			width: 34px;
			height: 34px;
			border-radius: 50%;
			background: #fdeaea;
			color: #dc3545;
			font-size: 15px;
			display: flex;
			align-items: center;
			justify-content: center;
		}

		.geo-body {
			flex: 1 1 auto;
			min-width: 0;
		}

		.geo-title {
			font-weight: 700;
			font-size: 15px;
			color: #212529;
			line-height: 1.25;
			margin-bottom: 3px;
		}

		.geo-text {
			font-size: 13.5px;
			color: #6c757d;
			line-height: 1.45;
		}

		.geo-actions {
			display: flex;
			flex-wrap: wrap;
			gap: 8px;
			margin-top: 12px;

			/* Los .btn de .buttons-wrapper son enormes (botones de fichar) */
			.btn {
				padding: 7px 14px;
				font-size: 13px;
				font-weight: 600;
				line-height: 1.2;
				border-radius: 8px;
			}
		}

		.geo-help {
			margin-top: 12px;
			padding: 11px 13px;
			background: #f7f9fc;
			border-radius: 8px;
			font-size: 13px;
			color: #495057;

			ol {
				margin: 0;
				padding-left: 17px;
			}

			li {
				margin-bottom: 5px;
				line-height: 1.45;

				&:last-child { margin-bottom: 0; }
			}
		}
	}

	.buttons-wrapper {

		.btn {
			padding: 30px 20px;
			font-size: 30px;
		}

		.last_in,
		.last_out {
			text-align: center;
			margin-bottom: 20px;

			.title {
				color: var(--bs-primary);
				font-weight: bold;
				font-size: 25px;
				line-height: 22px;
			}

			.time {
				font-weight: 500;
				font-size: 22px;
			}

			.reason {
				font-size: 18px;
			}
		}

		.radios-wrapper {
			label {
				display: flex;
				align-items: center;
				margin-bottom: 15px;
				font-size: 25px;

				input {
					width: 30px;
					height: 30px;
					margin-right: 10px;
				}
			}
		}
	}
`;

let cancelTokenSource = null;

// Texto de error legible de la API de geolocalización
function geoErrorMessage(err) {
	if ( err && err.code === 1 ) return 'No has dado permiso para acceder a tu ubicación';
	if ( err && err.code === 2 ) return 'No se ha podido obtener tu ubicación';
	if ( err && err.code === 3 ) return 'Tu ubicación ha tardado demasiado en obtenerse';
	return 'No se ha podido obtener tu ubicación';
}

// Los navegadores no dejan abrir sus ajustes desde la página, así que lo más
// que podemos hacer es explicar dónde están según el dispositivo.
function geoHelpSteps() {
	const ua = navigator.userAgent || '';

	if ( /iPad|iPhone|iPod/.test(ua) ) {
		return [
			'Abre Ajustes del iPhone y entra en Privacidad y seguridad > Localización. Comprueba que está activada.',
			'En esa misma lista busca el navegador (Safari o Chrome) y elige "Al usar la app".',
			'Vuelve aquí y pulsa "Activar ubicación".',
		];
	}

	if ( /Android/.test(ua) ) {
		return [
			'Desliza el dedo desde arriba de la pantalla y activa "Ubicación".',
			'En esta página, pulsa el icono del candado (o de aviso) que hay junto a la dirección web, entra en Permisos y activa "Ubicación".',
			'Vuelve aquí y pulsa "Activar ubicación".',
		];
	}

	return [
		'Pulsa el icono del candado (o de aviso) que hay a la izquierda de la dirección web.',
		'Busca "Ubicación" y ponlo en "Permitir".',
		'Vuelve aquí y pulsa "Activar ubicación".',
	];
}

export default function TimeControl() {
    // State
	const [data, setData] = useState(null);
	const [schedule, setSchedule] = useState(null);
	const [loading, setLoading] = useState(false);
	const [reason, setReason] = useState('');
	const [otherReasonOpened, setOtherReasonOpened] = useState(false);
	const [redirect, setRedirect] = useState(false);
	const [geoState, setGeoState] = useState(null); // null | granted | prompt | denied | unsupported
	const [geoHelpOpened, setGeoHelpOpened] = useState(false);
	const [geoChecking, setGeoChecking] = useState(false);

	// Refs
	const reasonInputTextRef = useRef(null);
	const geoPermissionRef = useRef(null);

	const loadData = useCallback(() => {
		setData(null);
		setLoading(true);

		axios.get('api/time-control/get-last', {
			params: {},
		    cancelToken: cancelTokenSource.token
	  	}).then((response) => {
	  		setData(response.data);
	  	}).catch((error) => {
	  		toast.error('Error al cargar los datos, por favor, vuelve a intentarlo');
	  	}).then(() => {
			setLoading(false);
	  	});
	}, []);

	// El horario va en su propia llamada a propósito: si el control de jornada
	// falla o el trabajador no lo tiene activado, la pantalla de fichar sigue
	// funcionando igual. Nunca debe impedir fichar.
	const loadSchedule = useCallback(() => {
		axios.get('api/attendance/today', {
			params: {},
			cancelToken: cancelTokenSource.token
		}).then((response) => {
			if ( response.data && response.data.enabled ) setSchedule(response.data);
		}).catch((error) => {
			setSchedule(null);
		});
	}, []);

	// Estado del permiso de ubicación. Solo sirve para avisar: aunque esté
	// denegado o el navegador no lo soporte, se puede fichar igualmente.
	const checkGeoPermission = useCallback(() => {
		if ( !navigator.geolocation ) {
			setGeoState('unsupported');
			return;
		}

		// Safari antiguo no tiene Permissions API: el estado se sabrá al fichar
		if ( !navigator.permissions || !navigator.permissions.query ) return;

		navigator.permissions.query({ name: 'geolocation' }).then((status) => {
			geoPermissionRef.current = status;
			setGeoState(status.state);
			status.onchange = () => setGeoState(status.state);
		}).catch(() => {});
	}, []);

	const getCoords = async (options = {}) => {
		if ( !navigator.geolocation ) {
			const err = new Error('Geolocalización no disponible');
			err.code = 0;
			throw err;
		}

        const pos = await new Promise((resolve, reject) => {
      		navigator.geolocation.getCurrentPosition(resolve, reject, {
				enableHighAccuracy: true,
				timeout: 10000,
				maximumAge: 60000,
				...options
			});
        });

        return {
          	long: pos.coords.longitude,
          	lat: pos.coords.latitude,
        };
    };

	// Pedir la ubicación desde el aviso. Si el permiso está en "preguntar",
	// esto abre el diálogo del navegador; si está denegado no aparece nada y
	// solo queda enseñar dónde cambiarlo a mano.
	const requestGeoPermission = async (e) => {
		e.preventDefault();

		setGeoChecking(true);

		try {
			await getCoords({ timeout: 15000 });
			setGeoState('granted');
			setGeoHelpOpened(false);
			toast.success('Ubicación activada');
		} catch (err) {
			if ( err.code === 1 ) {
				setGeoState('denied');
				setGeoHelpOpened(true);
			}
			toast.warning(geoErrorMessage(err));
		}

		setGeoChecking(false);
	};

	const sendAction = async (e, action) => {
		e.preventDefault();

		// Check reason (antes de pedir la ubicación, para no esperar al GPS en balde)
		if ( !reason.replace(/\s/g,'').length && action === "out" ) {
			toast.error('Debes elegir un motivo de salida');
			return false;
		}

		setLoading(true);

		// La ubicación es opcional: si no se puede obtener, se ficha igualmente
		// y el registro se guarda sin coordenadas.
	    let position = null;
	    try {
			position = await getCoords();
			setGeoState('granted');
		} catch (err) {
			if ( err.code === 1 ) setGeoState('denied');
			toast.warning(geoErrorMessage(err) + '. El fichaje quedará registrado como incompleto');
		}

		// Ajax
		axios.post('api/time-control/do-action/' + action, {
			reason: reason,
			latitude: position ? position.lat : null,
			longitude: position ? position.long : null
		}, {
			cancelToken: cancelTokenSource.token
		}).then((response) => {
			setData(response.data.last);
	  		setRedirect(true);
	  	}).catch((error) => {
	  		toast.error('Error al enviar los datos, por favor, vuelve a intentarlo');
	  	}).then(() => {
			setLoading(false);
	  	});		
	};

	useEffect(() => {
		// Set axios cancel token
		cancelTokenSource = axios.CancelToken.source();

		// Load data
		loadData();
		loadSchedule();
		checkGeoPermission();

		// On unmount, cancel any ajax request
  		return function cleanup() {
           	cancelTokenSource.cancel();
           	if ( geoPermissionRef.current ) geoPermissionRef.current.onchange = null;
        }
	}, [loadData, loadSchedule, checkGeoPermission]);
    
    useEffect(() => {
    	if ( otherReasonOpened ) reasonInputTextRef.current.focus();
    }, [otherReasonOpened]);
    
	// Prepare last_in
	let last_in_element = null;
	let date_in_str = "";
	if ( data && data.action === "in" ) {
		let date = moment(data.created_at);
		let today = moment().startOf('day');
		let yesterday = moment().subtract(1, 'days').startOf('day');

		if ( date.isSame(today, "d") ) date_in_str = "Hoy";
		if ( date.isSame(yesterday, "d") ) date_in_str = "Ayer";
		if ( !date_in_str ) date_in_str = date.format('DD-MM-YYYY');
		if ( date_in_str ) date_in_str = date_in_str + ' a las ' + date.format('HH:mm');
	}
	if ( date_in_str ) {
		last_in_element = 	<div className="last_in">
								<div className="title">Última entrada</div>
								<div className="time">{ date_in_str }</div>
							</div>;
	}


	// Prepare last_out
	let last_out_element = null;
	let date_out_str = "";
	if ( data && data.action === "out" ) {
		let date = moment(data.created_at);
		let today = moment().startOf('day');
		let yesterday = moment().subtract(1, 'days').startOf('day');

		if ( date.isSame(today, "d") ) date_out_str = "Hoy";
		if ( date.isSame(yesterday, "d") ) date_out_str = "Ayer";
		if ( !date_out_str ) date_out_str = date.format('DD-MM-YYYY');
		if ( date_out_str ) date_out_str = date_out_str + ' a las ' + date.format('HH:mm');
	}
	if ( date_out_str ) {
		last_out_element = 	<div className="last_out">
								<div className="title">Última salida</div>
								<div className="time">{ date_out_str }</div>
								<div className="reason">{ data.reason ? data.reason : ''}</div>
							</div>;
	}

	// Show toast before redirect
	useEffect(() => {
		if ( data ) toast.success((data.action === 'in' ? 'Entrada' : 'Salida') + ' realizada');
	}, [redirect]);

	// Redirect
	if ( redirect ) return <Redirect to="/" />;
	
	// Render
	return (
		<ContainerStyled className="container">
			<div className="row">
				<div className="col-md-12">
					<div className="page-title">
						<Link to="/" className="btn btn-primary" id="back-button"><FontAwesomeIcon icon={faChevronLeft} /></Link>
						<h4 className="p-0 m-0 py-2">Horario</h4>	
					</div>
				</div>

				{ /* Jornada de hoy. El fichaje es único: no se elige empresa al
				     pulsar, aquí solo se ve el horario combinado del día. */ }
				{ (!loading && schedule) &&
					<div className="col-md-12">
						{ schedule.leave &&
							<div className="section-card">
								<div className="section-title">Tu jornada de hoy</div>
								<div className="soc-block"><div className="off">{ schedule.leave.label }</div></div>
							</div>
						}
						{ (!schedule.leave && schedule.blocks.length > 0) &&
							<div className="section-card">
								<div className="section-title">Tu jornada de hoy</div>
								{ schedule.blocks.map((block, i) =>
									<div className="soc-block" key={i}>
										<div className="soc-h">
											<span className="dotc" style={{ background: block.color }}></span>
											{ block.company_name }
											{ block.schedule_name ? ' (' + block.schedule_name + ')' : '' }
										</div>
										{ block.working
											? <div className="jornada-grid">
												{ block.segments.map((segment, j) =>
													<React.Fragment key={j}>
														<div><div className="h">Entrada</div><div className="v">{ segment.in }</div></div>
														<div><div className="h">Salida</div><div className="v">{ segment.out }</div></div>
													</React.Fragment>
												) }
											  </div>
											: <div className="off">{ block.reason }</div>
										}
									</div>
								) }
							</div>
						}
					</div>
				}

				{ loading && loader() }
				{ !loading &&
					<div className="col-md-12 buttons-wrapper">
						{ (geoState === 'denied' || geoState === 'unsupported') &&
							<div className="aviso-geo">
								<div className="geo-icon"><FontAwesomeIcon icon={faExclamationTriangle} /></div>
								<div className="geo-body">
									<div className="geo-title">La ubicación no está activa</div>
									<div className="geo-text">
										{ geoState === 'unsupported'
											? 'Este dispositivo no permite obtener la ubicación, así que tu fichaje quedará registrado como incompleto. Avisa a tu responsable.'
											: 'Debes activar la ubicación para fichar correctamente. Los fichajes sin ubicación quedan registrados como incompletos y la empresa los revisa.'
										}
									</div>
									<div className="geo-actions">
										{ geoState !== 'unsupported' &&
											<button type="button" className="btn btn-danger" onClick={(e) => requestGeoPermission(e)} disabled={geoChecking}>
												{ geoChecking ? 'Comprobando...' : 'Activar ubicación' }
											</button>
										}
										<button type="button" className="btn btn-outline-secondary" onClick={() => setGeoHelpOpened(!geoHelpOpened)}>
											{ geoHelpOpened ? 'Ocultar ayuda' : 'Cómo activarla' }
										</button>
									</div>
									{ geoHelpOpened &&
										<div className="geo-help">
											<ol>
												{ geoHelpSteps().map((step, i) => <li key={i}>{ step }</li>) }
											</ol>
										</div>
									}
								</div>
							</div>
						}
						{ (schedule && schedule.warning && (!data || data.action === "out")) &&
							<div className="aviso">{ schedule.warning.message }</div>
						}
						{ (data && data.action === "in") && last_in_element }
						{ (data && data.action === "out") && last_out_element }

						{ (!data || data.action === "out") &&
							<a href="." className="btn btn-primary btn-lg d-block mb-2 in" onClick={(e) => sendAction(e, 'in')}>
								ENTRAR
							</a>
						}
						{ (data && data.action === "in") &&
							<React.Fragment>
								<hr />
								<div className="radios-wrapper">
									<label><input type="radio" name="reason" onChange={(e) => { setReason('Comida'); setOtherReasonOpened(false); }} /> Comida</label>
									<label><input type="radio" name="reason" onChange={(e) => { setReason('Fin jornada'); setOtherReasonOpened(false); }} /> Fin de jornada</label>
									<label><input type="radio" name="reason" onChange={(e) => { setReason('Médico'); setOtherReasonOpened(false); }} /> Médico</label>
									<label><input type="radio" name="reason" onChange={(e) => { setReason('Permiso'); setOtherReasonOpened(false); }} /> Permiso</label>
									<label><input type="radio" name="reason" onChange={(e) => { setReason(''); setOtherReasonOpened(true); }} /> Otro motivo</label>
									{ otherReasonOpened &&
										<input type="text" ref={reasonInputTextRef} name="reason" onChange={(e) => setReason(e.target.value)} className="form-control form-control-lg mb-3" placeholder="Escribe el motivo" />
									}
								</div>
								<a href="." className="btn btn-danger btn-lg d-block mb-2 out" onClick={(e) => sendAction(e, 'out')}>
									SALIR
								</a>
							</React.Fragment>
						}
					</div>
				}	
			</div>
		</ContainerStyled>	
	);
}